import './membership-shipping-billing.component.scss';

import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useMemo } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useTranslation } from '../../../i18n';
import { modalService } from '../../../modal/modal.service';
import type { AppDispatch } from '../../../store';
import { useAppSelector } from '../../../store/hooks';
import type { MembershipAddressModalProps } from '../../modals/membership-address.modal';
import { MembershipAddressModal } from '../../modals/membership-address.modal';
import type {
  Contract,
  Membership,
  MembershipContractAddressUpdateRequest,
} from '../../models/membership.model';
import { PaymentMethodType } from '../../models/membership.model';
import { selectAddressData } from '../../store/membership.selectors';
import { updateMembershipAddress, updatePaymentMethod } from '../../store/membership.thunk';
import { humanize } from '../../utils/membership.formatters';
import { isContractFailed, isMembershipOneTimePurchase } from '../../utils/membership.utlis';
import type { MembershipDetailRow } from '../membership-detail-table/membership-detail-table.component';
import { MembershipDetailTable } from '../membership-detail-table/membership-detail-table.component';

interface MembershipShippingBillingProps {
  membership: Membership;
}

const MembershipShippingBillingComponent: FunctionComponent<MembershipShippingBillingProps> = ({
  membership,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const countryOptions = useAppSelector(selectAddressData);
  const { t } = useTranslation();

  // Memoized computed values
  const contractIsFailed = useMemo(
    () => isContractFailed(membership?.contract),
    [membership?.contract]
  );

  // One-time (non-renewing) memberships have no recurring payment method to manage.
  const isOneTimePurchase = useMemo(() => isMembershipOneTimePurchase(membership), [membership]);

  const hasMissingAddress = useMemo(
    () => !membership?.contract?.streetLine1,
    [membership?.contract?.streetLine1]
  );

  // Memoized event handlers
  const handleUpdateAddress = useCallback(
    (contract: Contract) => {
      const addressModalProps: MembershipAddressModalProps = {
        contract: contract,
        countryOptions: countryOptions ?? '',
        onClose: async (address: MembershipContractAddressUpdateRequest | null) => {
          if (address) {
            try {
              await dispatch(updateMembershipAddress(address)).unwrap();
              modalService.closeAll();
            } catch (error) {
              // Keep modal open on error so user can retry or see the error state
              console.error('Failed to update address:', error);
            }
          } else {
            modalService.closeAll();
          }
        },
      };
      modalService.open({
        component: MembershipAddressModal,
        props: addressModalProps,
      });
    },
    [countryOptions, dispatch]
  );

  const handleSendPaymentUpdateEmail = useCallback(
    (contract: Contract) => {
      dispatch(updatePaymentMethod(contract));
    },
    [dispatch]
  );

  // SNGL-7386: one row per address field, from the contract's own fields rather than a formatted
  // string, so a missing unit, state or ZIP (common outside the US) drops its row instead of leaving a
  // dangling comma
  const shippingRows = (contract: Contract): MembershipDetailRow[] => {
    const name = [contract.firstName, contract.lastName].filter(Boolean).join(' ');
    const rows: MembershipDetailRow[] = [];
    if (name) rows.push({ key: 'name', label: t('membership.detailName'), value: <p>{name}</p> });
    if (!contract.streetLine1) return rows;
    rows.push({
      key: 'street',
      label: t('membership.detailStreet'),
      value: (
        <>
          <p>{contract.streetLine1}</p>
          {contract.streetLine2 && <p>{contract.streetLine2}</p>}
        </>
      ),
    });
    const fields: [string, string, string | undefined][] = [
      ['city', t('membership.detailCity'), contract.city],
      ['state', t('membership.detailState'), contract.province],
      ['zip', t('membership.detailZip'), contract.zipCode],
      ['country', t('membership.detailCountry'), contract.country],
      ['phone', t('membership.detailPhone'), contract.phoneNumber],
    ];
    fields
      .filter(([, , value]) => !!value)
      .forEach(([key, label, value]) => rows.push({ key, label, value: <p>{value}</p> }));
    return rows;
  };

  const creditCardRows = (contract: Contract): MembershipDetailRow[] => [
    {
      key: 'card',
      label: t('membership.detailCard'),
      value: (
        <div className="credit-card">
          <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 576 512">
            <path d="M512 80c8.8 0 16 7.2 16 16v32H48V96c0-8.8 7.2-16 16-16H512zm16 144V416c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V224H528zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm56 304c-13.3 0-24 10.7-24 24s10.7 24 24 24h48c13.3 0 24-10.7 24-24s-10.7-24-24-24H120zm128 0c-13.3 0-24 10.7-24 24s10.7 24 24 24H360c13.3 0 24-10.7 24-24s-10.7-24-24-24H248z" />
          </svg>
          <p>{humanize(contract.sourcePaymentMethodBrand)}</p>
        </div>
      ),
    },
    {
      key: 'number',
      label: t('membership.detailNumber'),
      value: <p>•••• {contract.sourcePaymentMethodLastDigits}</p>,
    },
    {
      key: 'expires',
      label: t('membership.detailExpires'),
      value: (
        <p>
          {contract.sourcePaymentMethodExpiryMonth} / {contract.sourcePaymentMethodExpiryYear}
        </p>
      ),
    },
  ];

  // Early return if no membership or contract
  if (!membership?.contract) {
    return null;
  }

  const { contract } = membership;

  return (
    <div className={`contract-detail-container ${isOneTimePurchase ? 'single-column' : ''}`}>
      {/* Shipping Address Card */}
      <div className="contract-detail">
        <div className="shipping-header">
          <h3>{t('membership.shippingAddress')}</h3>
        </div>
        <div className="contract-detail-content">
          {hasMissingAddress && (
            <button
              className="missing-info button btn"
              onClick={() => handleUpdateAddress(contract)}
            >
              <p>{t('membership.missingAddress')}</p>
            </button>
          )}
          <MembershipDetailTable rows={shippingRows(contract)} />
        </div>
        <button className="button btn" onClick={() => handleUpdateAddress(contract)}>
          {t('membership.editShippingAddress')}
        </button>
      </div>
      {/* Payment Method Card — recurring memberships only */}
      {!isOneTimePurchase && (
        <div className={`contract-detail payment ${contractIsFailed ? 'failed-contract' : ''}`}>
          <h3>{t('membership.paymentMethod')}</h3>
          <div className="contract-detail-content">
            {/* Credit Card */}
            {contract.sourcePaymentMethodType === PaymentMethodType.CREDIT_CARD && (
              <MembershipDetailTable rows={creditCardRows(contract)} />
            )}
            {/* ShopPay */}
            {contract.sourcePaymentMethodType === PaymentMethodType.SHOP_PAY && (
              <MembershipDetailTable
                rows={[
                  {
                    key: 'method',
                    label: t('membership.detailMethod'),
                    value: (
                      <div className="credit-card">
                        <svg
                          height="1em"
                          viewBox="0 0 683 164"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M454.942 0C441.175 0 430.015 11.1602 430.015 24.927V138.295C430.015 152.062 441.175 163.222 454.942 163.222H658.072C671.839 163.222 682.999 152.062 682.999 138.295V24.927C682.999 11.1602 671.839 0 658.072 0H454.942ZM490.023 113.902V85.1661H508.1C524.616 85.1661 533.399 75.9057 533.399 61.872C533.399 47.8383 524.616 39.4371 508.1 39.4371H478.376V113.902H490.023ZM490.023 50.5114H505.427C516.119 50.5114 521.37 54.9029 521.37 62.2539C521.37 69.6049 516.31 73.9964 505.904 73.9964H490.023V50.5114ZM553.933 115.429C562.811 115.429 568.635 111.515 571.308 104.832C572.072 112.279 576.559 116.098 586.296 113.52L586.392 105.596C582.478 105.978 581.714 104.546 581.714 100.441V80.9655C581.714 69.5094 574.172 62.7312 560.234 62.7312C546.486 62.7312 538.562 69.6049 538.562 81.2519H549.255C549.255 75.7148 553.169 72.3734 560.043 72.3734C567.298 72.3734 570.64 75.5239 570.544 80.9655V83.4477L558.229 84.7842C544.386 86.3117 536.749 91.5624 536.749 100.727C536.749 108.269 542.095 115.429 553.933 115.429ZM556.319 106.837C550.305 106.837 547.918 103.591 547.918 100.345C547.918 95.9539 552.883 93.9491 562.62 92.8035L570.258 91.9443C569.78 100.345 564.148 106.837 556.319 106.837ZM621.754 117.625C616.886 129.463 609.057 132.995 596.837 132.995H591.587V123.258H597.219C603.902 123.258 607.148 121.157 610.68 115.143L589.009 64.2587H601.038L616.504 101.396L630.251 64.2587H641.994L621.754 117.625Z"
                            fill="#121212"
                          />
                          <path
                            d="M57.3945 71.7445C41.4471 68.2852 34.3427 66.9315 34.3427 60.7862C34.3427 55.0063 39.1505 52.127 48.7662 52.127C57.2228 52.127 63.4042 55.8228 67.9545 63.0638C68.2979 63.6225 69.0062 63.8159 69.5857 63.5151L87.5292 54.4476C88.1731 54.1253 88.4092 53.3088 88.0443 52.6857C80.5965 39.7721 66.8384 32.7029 48.7232 32.7029C24.9203 32.7029 10.132 44.4347 10.132 63.0853C10.132 82.8962 28.1398 87.9027 44.1086 91.3621C60.0774 94.8215 67.2033 96.1751 67.2033 102.32C67.2033 108.466 62.0091 111.366 51.6423 111.366C42.0696 111.366 34.9652 106.983 30.6725 98.4742C30.3505 97.8511 29.5993 97.5933 28.9769 97.9156L11.0763 106.79C10.4539 107.112 10.1963 107.864 10.5183 108.509C17.6227 122.797 32.1964 130.833 51.6637 130.833C76.454 130.833 91.4355 119.295 91.4355 100.064C91.4355 80.8335 73.3418 75.2469 57.3945 71.7875V71.7445Z"
                            fill="#121212"
                          />
                          <path
                            d="M153.551 32.7032C143.377 32.7032 134.384 36.3129 127.924 42.7375C127.516 43.1243 126.85 42.845 126.85 42.2863V1.26785C126.85 0.558781 126.292 0.00012207 125.584 0.00012207H103.133C102.425 0.00012207 101.867 0.558781 101.867 1.26785V128.578C101.867 129.287 102.425 129.845 103.133 129.845H125.584C126.292 129.845 126.85 129.287 126.85 128.578V72.7332C126.85 61.9468 135.114 53.6743 146.253 53.6743C157.393 53.6743 165.463 61.7749 165.463 72.7332V128.578C165.463 129.287 166.021 129.845 166.729 129.845H189.18C189.889 129.845 190.447 129.287 190.447 128.578V72.7332C190.447 49.2695 175.079 32.7246 153.551 32.7246V32.7032Z"
                            fill="#121212"
                          />
                          <path
                            d="M235.991 29.0505C223.8 29.0505 212.381 32.7893 204.182 38.1825C203.624 38.5477 203.431 39.2998 203.774 39.8799L213.669 56.7901C214.034 57.3917 214.806 57.6066 215.407 57.2413C221.632 53.4811 228.758 51.5258 236.034 51.5688C255.63 51.5688 270.032 65.4063 270.032 83.6917C270.032 99.2697 258.506 110.808 243.889 110.808C231.977 110.808 223.714 103.868 223.714 94.0698C223.714 88.4618 226.096 83.8636 232.299 80.619C232.943 80.2753 233.179 79.4802 232.793 78.8571L223.456 63.0428C223.156 62.5271 222.512 62.2907 221.932 62.5056C209.419 67.1468 200.641 78.3199 200.641 93.3178C200.641 116.008 218.691 132.94 243.868 132.94C273.273 132.94 294.414 112.549 294.414 83.3049C294.414 51.9556 269.817 29.0505 235.991 29.0505Z"
                            fill="#121212"
                          />
                          <path
                            d="M360.069 32.5311C348.714 32.5311 338.584 36.7211 331.179 44.1126C330.771 44.5208 330.106 44.22 330.106 43.6613V34.7658C330.106 34.0567 329.548 33.498 328.839 33.498H306.968C306.26 33.498 305.702 34.0567 305.702 34.7658V161.882C305.702 162.591 306.26 163.15 306.968 163.15H329.419C330.127 163.15 330.685 162.591 330.685 161.882V120.198C330.685 119.639 331.35 119.36 331.758 119.725C339.142 126.601 348.908 130.619 360.09 130.619C386.426 130.619 406.966 109.282 406.966 81.5642C406.966 53.8461 386.404 32.5096 360.09 32.5096L360.069 32.5311ZM355.84 109.089C340.859 109.089 329.505 97.1637 329.505 81.3923C329.505 65.6209 340.837 53.6957 355.84 53.6957C370.843 53.6957 382.154 65.4275 382.154 81.3923C382.154 97.357 370.993 109.089 355.819 109.089H355.84Z"
                            fill="#121212"
                          />
                        </svg>
                      </div>
                    ),
                  },
                ]}
              />
            )}
            {/* PayPal */}
            {contract.sourcePaymentMethodType === PaymentMethodType.PAYPAL && (
              <MembershipDetailTable
                rows={[
                  {
                    key: 'method',
                    label: t('membership.detailMethod'),
                    value: (
                      <div className="credit-card">
                        <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 135 48">
                          <g clipPath="url(#a)">
                            <path
                              fill="#000"
                              d="M133 2.02v32.83h-7.47V2.02H133zM122.78 13.1v21.81h-6.64v-1.88c-.84.82-1.8 1.44-2.86 1.88-1.07.46-2.23.7-3.47.7-1.56 0-3.01-.29-4.34-.87-1.33-.61-2.49-1.44-3.47-2.48-.99-1.05-1.77-2.27-2.35-3.66-.55-1.42-.82-2.95-.82-4.58s.27-3.14.82-4.53c.58-1.42 1.36-2.66 2.35-3.71a10.45 10.45 0 0 1 3.47-2.44c1.33-.61 2.78-.92 4.34-.92 1.24 0 2.4.23 3.47.7 1.07.44 2.03 1.06 2.86 1.88v-1.88h6.64v-.02zm-11.77 15.87c1.36 0 2.47-.46 3.34-1.4.9-.93 1.35-2.12 1.35-3.57 0-1.45-.45-2.65-1.35-3.57-.87-.93-1.99-1.4-3.34-1.4-1.35 0-2.49.46-3.39 1.4-.87.93-1.31 2.12-1.31 3.57 0 1.45.44 2.65 1.31 3.57.9.93 2.03 1.4 3.39 1.4zM88.17 2.02c1.97 0 3.65.28 5.04.83 1.39.55 2.56 1.32 3.52 2.31.98 1.02 1.75 2.18 2.3 3.49.55 1.31.82 2.72.82 4.23 0 1.51-.27 2.92-.82 4.23a11.15 11.15 0 0 1-2.3 3.49c-.95.99-2.13 1.76-3.52 2.31-1.39.55-3.07.83-5.04.83h-3.6V34.9h-7.6V2.02h11.2zm-1.09 14.96c1.02 0 1.79-.1 2.35-.31.58-.23 1.06-.53 1.43-.87.78-.73 1.17-1.7 1.17-2.92s-.39-2.19-1.17-2.92c-.38-.35-.85-.63-1.43-.83-.55-.23-1.33-.35-2.35-.35h-2.52v8.2h2.52zM48.97 13.1h8.25l5.6 10.46h.09L67.9 13.1h7.64L59.17 45.98h-7.6l7.47-15.04L48.97 13.1zm-1.47 0v21.81h-6.64v-1.88c-.84.82-1.8 1.44-2.86 1.88-1.07.46-2.23.7-3.47.7-1.56 0-3.01-.29-4.34-.87-1.33-.61-2.49-1.44-3.47-2.48-.99-1.05-1.77-2.27-2.35-3.66-.55-1.42-.82-2.95-.82-4.58s.27-3.14.82-4.53c.58-1.42 1.36-2.66 2.35-3.71a10.35 10.35 0 0 1 3.47-2.44c1.33-.61 2.78-.92 4.34-.92 1.24 0 2.4.23 3.47.7 1.07.44 2.03 1.06 2.86 1.88v-1.88h6.64v-.02zM35.73 28.97c1.36 0 2.47-.46 3.35-1.4.9-.93 1.35-2.12 1.35-3.57 0-1.45-.45-2.65-1.35-3.57-.87-.93-1.99-1.4-3.35-1.4s-2.49.46-3.39 1.4c-.87.93-1.3 2.12-1.3 3.57 0 1.45.44 2.65 1.3 3.57.9.93 2.03 1.4 3.39 1.4zM13.2 2.02c1.97 0 3.65.28 5.04.83 1.39.55 2.56 1.32 3.52 2.31.98 1.02 1.75 2.18 2.3 3.49.55 1.31.82 2.72.82 4.23 0 1.51-.27 2.92-.82 4.23a11.15 11.15 0 0 1-2.3 3.49c-.95.99-2.13 1.76-3.52 2.31-1.39.55-3.07.83-5.04.83H9.6V34.9H2V2.02h11.2zm-1.08 14.96c1.02 0 1.79-.1 2.35-.31.58-.23 1.06-.53 1.43-.87.78-.73 1.17-1.7 1.17-2.92s-.39-2.19-1.17-2.92c-.38-.35-.85-.63-1.43-.83-.55-.23-1.33-.35-2.35-.35H9.6v8.2h2.52z"
                            />
                          </g>
                          <defs>
                            <clipPath id="a">
                              <path fill="#fff" d="M2 2.02h131v43.96H2z" />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    ),
                  },
                ]}
              />
            )}
            <p>{t('membership.updatePaymentInstructions')}</p>
          </div>
          <button className="button btn" onClick={() => handleSendPaymentUpdateEmail(contract)}>
            {t('membership.updatePaymentMethod')}
          </button>
        </div>
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const MembershipShippingBilling = memo(
  MembershipShippingBillingComponent,
  // every field is shown now, so compare the contract itself: an address save reloads the memberships
  // and hands back a new contract, where comparing only streetLine1 missed a changed city or ZIP
  (prevProps, nextProps) => prevProps.membership?.contract === nextProps.membership?.contract
);
